import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  getAllPageConfigs,
  createPageConfig,
  updatePageConfig,
  deletePageConfig,
  generateBookingUrl
} from '../../../services/bookingPageConfigService';

const Container = styled.div`
  width: 100%;
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  background: ${props => {
    if (props.type === 'success') return '#d4edda';
    if (props.type === 'error') return '#f8d7da';
    return '#d1ecf1';
  }};
  border: 1px solid ${props => {
    if (props.type === 'success') return '#c3e6cb';
    if (props.type === 'error') return '#f5c6cb';
    return '#bee5eb';
  }};
  color: ${props => {
    if (props.type === 'success') return '#155724';
    if (props.type === 'error') return '#721c24';
    return '#0c5460';
  }};
  font-weight: 500;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid;
  
  ${props => {
    if (props.variant === 'primary') {
      return `
        background: ${props.theme.colors.primary};
        color: white;
        border-color: ${props.theme.colors.primary};
        &:hover { 
          background: ${props.theme.colors.accent};
          border-color: ${props.theme.colors.accent};
        }
      `;
    }
    if (props.variant === 'danger') {
      return `
        background: #dc3545;
        color: white;
        border-color: #dc3545;
        &:hover { background: #c82333; border-color: #c82333; }
      `;
    }
    return `
      background: white;
      color: ${props.theme.colors.primary};
      border-color: ${props.theme.colors.primary};
      &:hover { 
        background: ${props.theme.colors.offWhite};
      }
    `;
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const Th = styled.th`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.9rem;
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.9rem;
  color: #333;
`;

const Tr = styled.tr`
  &:hover {
    background: ${props => props.theme.colors.offWhite};
  }
  
  &:last-child td {
    border-bottom: none;
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => props.active ? '#d4edda' : '#f8d7da'};
  color: ${props => props.active ? '#155724' : '#721c24'};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const SmallButton = styled.button`
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid;
  
  ${props => {
    if (props.variant === 'edit') {
      return `
        background: #007bff;
        color: white;
        border-color: #007bff;
        &:hover { background: #0056b3; }
      `;
    }
    if (props.variant === 'delete') {
      return `
        background: #dc3545;
        color: white;
        border-color: #dc3545;
        &:hover { background: #c82333; }
      `;
    }
    if (props.variant === 'copy') {
      return `
        background: #28a745;
        color: white;
        border-color: #28a745;
        &:hover { background: #218838; }
      `;
    }
    return `
      background: white;
      color: #666;
      border-color: #ddd;
      &:hover { background: #f8f9fa; }
    `;
  }}
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
`;

const ModalTitle = styled.h3`
  margin: 0 0 1.5rem 0;
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
  
  &:disabled {
    background: #f8f9fa;
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.95rem;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-weight: 500;
  color: #333;
  
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  justify-content: flex-end;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #666;
  
  p {
    margin: 0.5rem 0;
    font-size: 1rem;
  }
  
  .icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }
`;

const CopyableUrl = styled.div`
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 6px;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.85rem;
  word-break: break-all;
  margin-top: 0.5rem;
  border: 1px solid #e9ecef;
`;

const BookingPageManager = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    page_id: '',
    page_name: '',
    location_id: '978',
    family_saver_enabled: true,
    booking_url: '',
    notes: ''
  });

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      setLoading(true);
      const data = await getAllPageConfigs();
      
      // Handle both array and object responses
      if (Array.isArray(data)) {
        setPages(data);
      } else if (data && Array.isArray(data.data)) {
        setPages(data.data);
      } else {
        setPages([]);
        if (data && !data.success) {
          showAlert('error', 'Failed to load booking pages. WordPress may not be running.');
        }
      }
    } catch (error) {
      console.error('Error loading pages:', error);
      showAlert('error', 'Failed to load booking pages: ' + error.message);
      setPages([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleCreate = () => {
    setEditingPage(null);
    setFormData({
      page_id: '',
      page_name: '',
      location_id: '978',
      family_saver_enabled: true,
      booking_url: '',
      notes: ''
    });
    setShowModal(true);
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      page_id: page.page_id,
      page_name: page.page_name,
      location_id: page.location_id || '978',
      family_saver_enabled: page.family_saver_enabled,
      booking_url: page.booking_url || '',
      notes: page.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (page) => {
    if (!window.confirm(`Are you sure you want to delete "${page.page_name}"?`)) {
      return;
    }

    try {
      await deletePageConfig(page.id);
      showAlert('success', `Deleted "${page.page_name}" successfully`);
      loadPages();
    } catch (error) {
      showAlert('error', 'Failed to delete page: ' + error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Auto-generate booking URL if not provided
    const dataToSubmit = { ...formData };
    if (!dataToSubmit.booking_url && dataToSubmit.page_id && dataToSubmit.location_id) {
      dataToSubmit.booking_url = generateBookingUrl(dataToSubmit.location_id, dataToSubmit.page_id);
    }

    try {
      if (editingPage) {
        await updatePageConfig(editingPage.id, dataToSubmit);
        showAlert('success', `Updated "${formData.page_name}" successfully`);
      } else {
        await createPageConfig(dataToSubmit);
        showAlert('success', `Created "${formData.page_name}" successfully`);
      }
      setShowModal(false);
      loadPages();
    } catch (error) {
      showAlert('error', error.message);
    }
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    showAlert('success', 'Booking URL copied to clipboard!');
  };

  const handleGenerateUrl = () => {
    if (formData.page_id && formData.location_id) {
      const url = generateBookingUrl(formData.location_id, formData.page_id);
      setFormData({ ...formData, booking_url: url });
    }
  };

  return (
    <Container>
      {alert && (
        <Alert type={alert.type}>
          {alert.message}
        </Alert>
      )}

      <ActionBar>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#101d40' }}>
            📋 Booking Pages ({pages.length})
          </h3>
          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#666' }}>
            Manage booking journey configurations and settings
          </p>
        </div>
        <Button variant="primary" onClick={handleCreate}>
          + Create New Page
        </Button>
      </ActionBar>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          Loading booking pages...
        </div>
      ) : pages.length === 0 ? (
        <EmptyState>
          <div className="icon">📄</div>
          <p><strong>No booking pages configured yet</strong></p>
          <p>Create your first booking page to get started</p>
          <Button variant="primary" onClick={handleCreate} style={{ marginTop: '1rem' }}>
            + Create First Page
          </Button>
        </EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Page Name</Th>
              <Th>Page ID</Th>
              <Th>Location ID</Th>
              <Th>Family Saver</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {pages.map(page => (
              <Tr key={page.id}>
                <Td>
                  <strong>{page.page_name}</strong>
                  {page.notes && (
                    <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                      {page.notes}
                    </div>
                  )}
                </Td>
                <Td>
                  <code style={{ fontSize: '0.8rem', background: '#f8f9fa', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>
                    {page.page_id}
                  </code>
                </Td>
                <Td>{page.location_id || 'N/A'}</Td>
                <Td>
                  <Badge active={page.family_saver_enabled}>
                    {page.family_saver_enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </Td>
                <Td>
                  <ButtonGroup>
                    {page.booking_url && (
                      <SmallButton 
                        variant="copy" 
                        onClick={() => handleCopyUrl(page.booking_url)}
                        title="Copy booking URL"
                      >
                        📋 Copy URL
                      </SmallButton>
                    )}
                    <SmallButton variant="edit" onClick={() => handleEdit(page)}>
                      ✏️ Edit
                    </SmallButton>
                    <SmallButton variant="delete" onClick={() => handleDelete(page)}>
                      🗑️ Delete
                    </SmallButton>
                  </ButtonGroup>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      {showModal && (
        <Modal onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              {editingPage ? 'Edit Booking Page' : 'Create New Booking Page'}
            </ModalTitle>

            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Page Name *</Label>
                <Input
                  type="text"
                  value={formData.page_name}
                  onChange={(e) => setFormData({ ...formData, page_name: e.target.value })}
                  placeholder="e.g., Castle Visit, Afternoon Tea"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Page ID (GUID) *</Label>
                <Input
                  type="text"
                  value={formData.page_id}
                  onChange={(e) => setFormData({ ...formData, page_id: e.target.value })}
                  placeholder="e.g., 278ef0c8-a65b-4e31-9726-305ae06a5a14"
                  disabled={!!editingPage}
                  required
                />
                {editingPage && (
                  <small style={{ color: '#666', fontSize: '0.8rem' }}>
                    Page ID cannot be changed after creation
                  </small>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Location ID</Label>
                <Input
                  type="text"
                  value={formData.location_id}
                  onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                  placeholder="978"
                />
              </FormGroup>

              <FormGroup>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={formData.family_saver_enabled}
                    onChange={(e) => setFormData({ ...formData, family_saver_enabled: e.target.checked })}
                  />
                  Enable Family Saver Discount
                </CheckboxLabel>
              </FormGroup>

              <FormGroup>
                <Label>Booking URL</Label>
                <Input
                  type="url"
                  value={formData.booking_url}
                  onChange={(e) => setFormData({ ...formData, booking_url: e.target.value })}
                  placeholder="Auto-generated if left empty"
                />
                {formData.page_id && formData.location_id && (
                  <Button 
                    type="button" 
                    onClick={handleGenerateUrl}
                    style={{ marginTop: '0.5rem', fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                  >
                    🔗 Generate URL
                  </Button>
                )}
                {formData.booking_url && (
                  <CopyableUrl>
                    {formData.booking_url}
                  </CopyableUrl>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Internal notes about this booking page..."
                />
              </FormGroup>

              <ModalActions>
                <Button type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  {editingPage ? 'Update Page' : 'Create Page'}
                </Button>
              </ModalActions>
            </form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default BookingPageManager;

